import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { BiArrowBack } from 'react-icons/bi';
import { parivartanAPI } from '../../../services/parivartanAPI';
import { platformAPI } from '../../../services/platformAPI';
import { documentAPI } from '../../../services/documentAPI';
import tenderAPI from '../../../services/tenderAPI';
import MasterLookup, { useMasterOptions } from '../../../components/workspace/MasterLookup';
import EvidencePicker from './EvidencePicker';
import AiAssistNote from '../../../components/workspace/AiAssistNote';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { canEditTender } from '../../../utils/tenderRecordAccess';
import { useAltF1TestFill } from '../../../hooks/useAltF1TestFill';
import {
  dummyParivartanKnowledgeCredForm,
  dummyParivartanKnowledgeProjectForm,
  dummyParivartanKnowledgeReusableForm
} from '../../../utils/testFormDummies';
import { activeImportValues, keepCurrent, loadOrganisationSettings } from '../../../utils/organisationSettings';
import './CustomerMaster.scss';
import './PersonnelPage.scss';
import './KnowledgeRecordPage.scss';

const FALLBACK_CATEGORIES = ['Certification', 'Registration', 'Insurance', 'Accreditation', 'Other'];

const Field = ({ label, wide, children }) => (
  <Form.Group className={`cust-field${wide ? ` cust-field--${wide}` : ''}`}>
    <Form.Label>{label}</Form.Label>
    {children}
  </Form.Group>
);

const dateInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const kindFromPath = (pathname) => {
  if (pathname.includes('/projects')) return 'projects';
  if (pathname.includes('/reusable')) return 'reusable';
  return 'credentials';
};

const LIST_LABEL = {
  credentials: 'Credentials',
  projects: 'Project experience',
  reusable: 'Reusable content'
};

const KnowledgeRecordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { recordId } = useParams();
  const kind = kindFromPath(location.pathname);
  const isCreate = location.pathname.endsWith('/new');
  const isEdit = location.pathname.endsWith('/edit');
  const isView = !isCreate && !isEdit;
  const masters = useMasterOptions();
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [assisting, setAssisting] = useState(false);
  const [error, setError] = useState('');
  const [aiNote, setAiNote] = useState(null);
  const [activeTenders, setActiveTenders] = useState([]);
  const [links, setLinks] = useState([]);
  const [attachTenders, setAttachTenders] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [documentType, setDocumentType] = useState('');
  const [credentialFile, setCredentialFile] = useState(null);
  const [evidenceDocs, setEvidenceDocs] = useState([]);
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);

  const listPath = kind === 'credentials' ? '/apps/tender/knowledge' : `/apps/tender/knowledge?section=${kind}`;
  const heading = isCreate
    ? `New ${kind === 'projects' ? 'project' : kind === 'reusable' ? 'content' : 'credential'}`
    : (form.title || form.projectName || LIST_LABEL[kind]);

  useDocumentTitle(heading);

  useEffect(() => {
    if (kind !== 'credentials') return undefined;
    platformAPI.listReference('document-types', { area: 'knowledge' })
      .then((response) => {
        const values = (response.data?.data?.values || []).filter((row) => row.status === 'active');
        setDocumentTypes(values);
        setDocumentType((current) => current || values[0]?.code || '');
      })
      .catch(() => setDocumentTypes([]));
    return undefined;
  }, [kind]);

  useEffect(() => {
    if (kind !== 'credentials') return undefined;
    let cancelled = false;
    loadOrganisationSettings().then((data) => {
      if (cancelled) return;
      const next = activeImportValues(data?.settings?.importLists?.credentialCategories);
      const list = next.length ? next : FALLBACK_CATEGORIES;
      setCategories(list);
      if (isCreate) {
        setForm((prev) => ({
          ...prev,
          category: list.includes(prev.category) ? prev.category : (list[0] || 'Certification')
        }));
      }
    });
    return () => { cancelled = true; };
  }, [kind, isCreate]);

  useEffect(() => {
    if (isCreate) {
      if (kind === 'projects') setForm({ projectName: '', client: '', sector: '', serviceType: '', projectLocation: '', startDate: '', completionDate: '', completionStatus: '', valueInr: '', roadLengthKm: '', laneConfiguration: '', civilProjectValue: '', consultancyFee: '', participationShare: '', firmRole: '', completionEvidenceNotes: '', isActive: true });
      else if (kind === 'reusable') setForm({ title: '', classification: '', content: '', version: '1.0', approvalStatus: 'draft' });
      else setForm({ title: '', category: categories[0] || 'Certification', issuer: '', validTo: '', version: '1.0' });
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const response = kind === 'projects'
          ? await parivartanAPI.getProject(recordId)
          : kind === 'reusable'
            ? await parivartanAPI.getReusable(recordId)
            : await parivartanAPI.getCredential(recordId);
        const row = response.data?.data?.project || response.data?.data?.content || response.data?.data?.credential;
        if (!cancelled && row) {
          if (kind === 'projects') {
            setForm({
              projectName: row.projectName || '',
              client: row.client || '',
              sector: row.sector || '',
              serviceType: row.serviceType || '',
              projectLocation: row.projectLocation || '',
              startDate: dateInput(row.startDate),
              valueInr: row.valueInr ?? '',
              roadLengthKm: row.roadLengthKm ?? '',
              laneConfiguration: row.laneConfiguration || '',
              civilProjectValue: row.civilProjectValue ?? '',
              civilProjectCurrency: row.civilProjectCurrency || 'INR',
              consultancyFee: row.consultancyFee ?? '',
              consultancyFeeCurrency: row.consultancyFeeCurrency || 'INR',
              participationShare: row.participationShare ?? '',
              firmRole: row.firmRole || '',
              completionStatus: row.completionStatus || '',
              completionDate: dateInput(row.completionDate),
              completionEvidenceNotes: row.completionEvidenceNotes || '',
              isActive: row.isActive !== false
            });
            const evidenceIds = row.evidenceDocumentIds?.length
              ? row.evidenceDocumentIds
              : (row.evidenceDocumentId ? [row.evidenceDocumentId] : []);
            setEvidenceDocs(evidenceIds.map((id) => ({ documentId: id, name: 'Evidence document', version: 1 })));
          } else if (kind === 'reusable') {
            setForm({
              title: row.title || '',
              classification: row.classification || '',
              content: row.content || '',
              version: row.version || '1.0',
              approvalStatus: row.approvalStatus || 'draft'
            });
          } else {
            setForm({
              title: row.title || '',
              category: row.category || 'Certification',
              issuer: row.issuer || '',
              validTo: dateInput(row.validTo),
              version: row.version || '1.0'
            });
          }
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || 'Unable to load this record');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isCreate, kind, recordId]);

  useEffect(() => {
    if (kind !== 'credentials' || isCreate) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [tenderRes, linkRes] = await Promise.all([
          tenderAPI.getTenders().catch(() => ({})),
          parivartanAPI.listTenderCredentials()
        ]);
        if (cancelled) return;
        const tenderRows = tenderRes?.data?.tenders || tenderRes?.tenders || [];
        setActiveTenders(tenderRows.filter(canEditTender));
        setLinks((linkRes.data?.data?.links || []).filter((item) => String(item.credentialId) === String(recordId)));
      } catch {
        if (!cancelled) setLinks([]);
      }
    })();
    return () => { cancelled = true; };
  }, [kind, isCreate, recordId]);

  useAltF1TestFill(isCreate || isEdit, () => {
    if (kind === 'projects') setForm({ ...dummyParivartanKnowledgeProjectForm(), valueInr: '', completionDate: '', completionEvidenceNotes: '' });
    else if (kind === 'reusable') setForm({ ...dummyParivartanKnowledgeReusableForm(), approvalStatus: 'draft' });
    else setForm({ ...dummyParivartanKnowledgeCredForm(), version: '1.0' });
  });

  const setField = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const tenderOptions = useMemo(
    () => activeTenders
      .filter((item) => !links.some((link) => String(link.tenderId) === String(item._id || item.id)))
      .map((item) => ({
        value: item._id || item.id,
        label: `${item.reference || 'Tender'} — ${item.title || 'Untitled'}`,
        hint: item.organization || '',
        aliases: [item.reference, item.title, item.organization].filter(Boolean)
      })),
    [activeTenders, links]
  );

  const suggestWording = async () => {
    if (!String(form.title || '').trim()) {
      setError('Enter a title before asking for wording');
      return;
    }
    setAssisting(true);
    setError('');
    setAiNote(null);
    try {
      const response = await platformAPI.suggestReusableContent({
        title: form.title.trim(),
        classification: form.classification || '',
        content: form.content || ''
      });
      const data = response.data?.data || {};
      if (data.content) setForm((prev) => ({ ...prev, content: data.content }));
      setAiNote(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to suggest wording');
    } finally {
      setAssisting(false);
    }
  };

  const save = async (event) => {
    event.preventDefault();
    if (kind === 'projects' ? !String(form.projectName || '').trim() : !String(form.title || '').trim()) return;
    setSaving(true);
    setError('');
    try {
      if (kind === 'projects') {
        const blankNumber = (value) => (value === '' || value == null ? null : Number(value));
        const payload = {
          projectName: form.projectName.trim(),
          client: form.client || '',
          sector: form.sector || '',
          serviceType: form.serviceType || '',
          projectLocation: form.projectLocation || '',
          startDate: form.startDate || null,
          completionDate: form.completionDate || null,
          completionStatus: form.completionStatus || '',
          valueInr: blankNumber(form.valueInr),
          roadLengthKm: blankNumber(form.roadLengthKm),
          laneConfiguration: form.laneConfiguration || '',
          civilProjectValue: blankNumber(form.civilProjectValue),
          civilProjectCurrency: form.civilProjectCurrency || 'INR',
          consultancyFee: blankNumber(form.consultancyFee),
          consultancyFeeCurrency: form.consultancyFeeCurrency || 'INR',
          participationShare: blankNumber(form.participationShare),
          firmRole: form.firmRole || '',
          completionEvidenceNotes: form.completionEvidenceNotes || '',
          isActive: form.isActive !== false,
          evidenceDocumentIds: evidenceDocs.map((item) => item.documentId).filter(Boolean)
        };
        if (isCreate) {
          const response = await parivartanAPI.upsertProject(payload);
          const id = response.data?.data?.project?._id;
          navigate(id ? `/apps/tender/knowledge/projects/${id}/edit` : listPath);
        } else {
          await parivartanAPI.upsertProject({ ...payload, id: recordId });
          navigate(listPath);
        }
      } else if (kind === 'reusable') {
        const payload = {
          title: form.title.trim(),
          classification: form.classification || undefined,
          content: form.content || '',
          version: form.version || '1.0',
          approvalStatus: form.approvalStatus || 'draft'
        };
        if (isCreate) {
          const response = await parivartanAPI.upsertReusable(payload);
          const id = response.data?.data?.content?._id;
          navigate(id ? `/apps/tender/knowledge/reusable/${id}/edit` : listPath);
        } else {
          await parivartanAPI.upsertReusable({ ...payload, id: recordId });
          navigate(listPath);
        }
      } else {
        let documentId;
        if (credentialFile && documentType) {
          const body = new FormData();
          body.append('document', credentialFile);
          body.append('name', credentialFile.name);
          body.append('typeCode', documentType);
          body.append('documentArea', 'knowledge');
          body.append('accessClassification', 'standard');
          const uploaded = await documentAPI.uploadDocument(body);
          documentId = uploaded.data?.data?.document?._id;
          if (documentId) {
            await platformAPI.classifyDocument(documentId, { accessClassification: 'standard', aiContextEligible: false });
          }
        }
        const payload = {
          title: form.title.trim(),
          category: form.category,
          issuer: form.issuer.trim() || undefined,
          validTo: form.validTo || undefined,
          version: form.version || '1.0',
          documentId
        };
        if (isCreate) {
          const response = await parivartanAPI.upsertCredential(payload);
          const id = response.data?.data?.credential?._id;
          navigate(id ? `/apps/tender/knowledge/credentials/${id}/edit` : listPath);
        } else {
          await parivartanAPI.upsertCredential({ ...payload, id: recordId });
          navigate(listPath);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save this record');
    } finally {
      setSaving(false);
    }
  };

  const attach = async () => {
    if (!recordId || !attachTenders.length) {
      setError('Choose at least one active tender.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await parivartanAPI.attachTenderCredentials({ credentialId: recordId, tenderIds: attachTenders });
      setAttachTenders([]);
      const next = await parivartanAPI.listTenderCredentials();
      setLinks((next.data?.data?.links || []).filter((item) => String(item.credentialId) === String(recordId)));
    } catch (err) {
      setError(err.response?.data?.message || 'Could not attach this credential');
    } finally {
      setSaving(false);
    }
  };

  const detach = async (id) => {
    setSaving(true);
    setError('');
    try {
      await parivartanAPI.detachTenderCredential(id);
      setLinks((items) => items.filter((item) => String(item._id) !== String(id)));
    } catch (err) {
      setError(err.response?.data?.message || 'Could not remove this credential from the tender');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner animation="border" size="sm" />;

  const locked = isView;
  const editPath = `/apps/tender/knowledge/${kind}/${recordId}/edit`;

  return (
    <div className="cust-page">
      <form className={`cust-record pers-record${locked ? ' cust-record--view' : ''}`} onSubmit={save}>
        <header className="cust-mast">
          <div className="cust-mast__identity">
            <button type="button" className="cust-page__back" onClick={() => navigate(listPath)}>
              <BiArrowBack />
              Knowledge bank
            </button>
            <div className="cust-mast__row">
              {kind === 'credentials' ? <span className="cust-mark" aria-hidden>CR</span> : null}
              <div className="cust-mast__copy">
                <p className="cust-mast__eyebrow">{isEdit ? `Editing ${LIST_LABEL[kind].toLowerCase()}` : LIST_LABEL[kind]}</p>
                <h1>{heading}</h1>
              </div>
            </div>
          </div>
          <div className="cust-mast__actions">
            {isView ? (
              <Button size="sm" variant="primary" type="button" onClick={() => navigate(editPath)}>Edit</Button>
            ) : (
              <>
                <Button size="sm" variant="outline-primary" type="button" onClick={() => navigate(listPath)} disabled={saving}>Cancel</Button>
                <Button size="sm" variant="primary" type="submit" disabled={saving}>
                  {saving ? 'Saving…' : isCreate ? 'Create' : 'Save changes'}
                </Button>
              </>
            )}
          </div>
        </header>

        {error ? <Alert variant="danger" className="cust-record__alert">{error}</Alert> : null}
        <AiAssistNote result={aiNote} />

        <div className="cust-record__body">
          <div className="cust-record__main">
            {kind === 'credentials' ? (
              <div className="cred-sheet">
                <section className="cust-section cred-band">
                  <header className="cust-section__head">
                    <span>01</span>
                    <div>
                      <h2>Credential</h2>
                      <p>A registration, certificate, or insurance policy kept in the firm library.</p>
                    </div>
                  </header>
                  <div className="cred-identity">
                    <Field label="Title" wide="12">
                      <Form.Control value={form.title || ''} onChange={setField('title')} required disabled={locked} placeholder="ISO 9001 certificate" />
                    </Field>
                    <div className="cred-cats" role="group" aria-label="Category">
                      {keepCurrent(categories, form.category, FALLBACK_CATEGORIES).map((item) => {
                        const on = (form.category || categories[0] || 'Certification') === item;
                        return (
                          <button
                            key={item}
                            type="button"
                            className={on ? 'cred-cat is-on' : 'cred-cat'}
                            aria-pressed={on}
                            disabled={locked}
                            onClick={() => setForm((prev) => ({ ...prev, category: item }))}
                          >
                            {item}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="cred-facts">
                    <Field label="Issuer">
                      <Form.Control value={form.issuer || ''} onChange={setField('issuer')} disabled={locked} placeholder="Issuing body" />
                    </Field>
                    <Field label="Valid until">
                      <Form.Control type="date" value={form.validTo || ''} onChange={setField('validTo')} disabled={locked} />
                    </Field>
                    <Field label="Version">
                      <Form.Control value={form.version || ''} onChange={setField('version')} disabled={locked} />
                    </Field>
                  </div>
                </section>

                {!locked ? (
                  <section className="cust-section cred-band">
                    <header className="cust-section__head">
                      <span>02</span>
                      <div>
                        <h2>Evidence</h2>
                        <p>The document type names the file. It does not grant access and it does not make the file eligible for AI.</p>
                      </div>
                    </header>
                    <div className="cred-evidence">
                      <Field label="Document type">
                        <Form.Select aria-label="Document type" value={documentType} onChange={(event) => setDocumentType(event.target.value)}>
                          {documentTypes.map((row) => <option key={row.code} value={row.code}>{row.label}</option>)}
                        </Form.Select>
                      </Field>
                      <label className={credentialFile ? 'cred-file is-set' : 'cred-file'}>
                        <input type="file" onChange={(event) => setCredentialFile(event.target.files?.[0] || null)} />
                        <span className="cred-file__mark" aria-hidden>{credentialFile ? 'OK' : 'File'}</span>
                        <span className="cred-file__copy">
                          <strong>{credentialFile ? credentialFile.name : 'Choose the certificate file'}</strong>
                          <em>{credentialFile ? 'Ready to store with this credential' : 'PDF, Word, or a scan'}</em>
                        </span>
                      </label>
                    </div>
                  </section>
                ) : null}
              </div>
            ) : null}

            {kind === 'projects' ? (
              <section className="cust-section">
                <header className="cust-section__head">
                  <span>01</span>
                  <div>
                    <h2>Project experience</h2>
                    <p>A completed or ongoing assignment the firm can cite. Civil project value and the consultancy fee stay separate, and a blank amount is not treated as zero.</p>
                  </div>
                </header>
                <div className="cust-fields">
                  <Field label="Project name" wide="12">
                    <Form.Control value={form.projectName || ''} onChange={setField('projectName')} required disabled={locked} />
                  </Field>
                  <Field label="Client" wide="4">
                    <MasterLookup options={masters.customers} value={form.client || ''} onChange={(client) => setForm((prev) => ({ ...prev, client }))} placeholder="Search customers" disabled={locked || !masters.ready} />
                  </Field>
                  <Field label="Sector" wide="4">
                    <MasterLookup options={masters.sectors} value={form.sector || ''} onChange={(sector) => setForm((prev) => ({ ...prev, sector }))} placeholder="Search sectors" disabled={locked || !masters.ready} />
                  </Field>
                  <Field label="Service" wide="4">
                    <MasterLookup options={masters.services} value={form.serviceType || ''} onChange={(serviceType) => setForm((prev) => ({ ...prev, serviceType }))} placeholder="Search services" disabled={locked || !masters.ready} />
                  </Field>
                  <Field label="Location" wide="4">
                    <Form.Control value={form.projectLocation || ''} onChange={setField('projectLocation')} disabled={locked} />
                  </Field>
                  <Field label="Status" wide="4">
                    <Form.Select value={form.completionStatus || ''} onChange={setField('completionStatus')} disabled={locked}>
                      <option value="">Not stated</option>
                      <option value="ongoing">Ongoing</option>
                      <option value="completed">Completed</option>
                    </Form.Select>
                  </Field>
                  <Field label="Record" wide="4">
                    <Form.Select value={form.isActive === false ? 'inactive' : 'active'} onChange={(event) => setForm((prev) => ({ ...prev, isActive: event.target.value !== 'inactive' }))} disabled={locked}>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </Form.Select>
                  </Field>
                  <Field label="Started" wide="4">
                    <Form.Control type="date" value={form.startDate || ''} onChange={setField('startDate')} disabled={locked} />
                  </Field>
                  <Field label="Completed" wide="4">
                    <Form.Control type="date" value={form.completionDate || ''} onChange={setField('completionDate')} disabled={locked} />
                  </Field>
                  <Field label="Dhruv role" wide="4">
                    <Form.Control value={form.firmRole || ''} onChange={setField('firmRole')} disabled={locked} placeholder="PMC, DPR, or the role on this job" />
                  </Field>
                  <Field label="Road length (km)" wide="4">
                    <Form.Control type="number" min="0" value={form.roadLengthKm ?? ''} onChange={setField('roadLengthKm')} disabled={locked} placeholder="Leave blank if unknown" />
                  </Field>
                  <Field label="Lanes" wide="4">
                    <Form.Control value={form.laneConfiguration || ''} onChange={setField('laneConfiguration')} disabled={locked} placeholder="4 lane" />
                  </Field>
                  <Field label="Civil project value" wide="4">
                    <Form.Control type="number" min="0" value={form.civilProjectValue ?? ''} onChange={setField('civilProjectValue')} disabled={locked} placeholder="Works value, not the fee" />
                  </Field>
                  <Field label="Consultancy fee" wide="4">
                    <Form.Control type="number" min="0" value={form.consultancyFee ?? ''} onChange={setField('consultancyFee')} disabled={locked} placeholder="Dhruv fee, if known" />
                  </Field>
                  <Field label="Dhruv share %" wide="4">
                    <Form.Control type="number" min="0" max="100" value={form.participationShare ?? ''} onChange={setField('participationShare')} disabled={locked} placeholder="Blank if unknown" />
                  </Field>
                  <Field label="Recorded value (INR)" wide="4">
                    <Form.Control type="number" min="0" value={form.valueInr ?? ''} onChange={setField('valueInr')} disabled={locked} placeholder="Earlier single figure, if any" />
                  </Field>
                  <Field label="Notes" wide="12">
                    <Form.Control as="textarea" rows={4} value={form.completionEvidenceNotes || ''} onChange={setField('completionEvidenceNotes')} disabled={locked} placeholder="What was delivered, and where the proof is kept" />
                  </Field>
                  <div className="cust-field cust-field--12">
                    <EvidencePicker
                      area="knowledge"
                      label="Evidence documents"
                      classification="restricted"
                      documents={evidenceDocs}
                      onChange={locked ? () => {} : setEvidenceDocs}
                    />
                  </div>
                </div>
              </section>
            ) : null}

            {kind === 'reusable' ? (
              <section className="cust-section">
                <header className="cust-section__head">
                  <span>01</span>
                  <div>
                    <h2>Reusable content</h2>
                    <p>A paragraph the technical team can reuse. New text is saved as a draft.</p>
                  </div>
                </header>
                <div className="cust-fields">
                  <Field label="Title" wide="8">
                    <Form.Control value={form.title || ''} onChange={setField('title')} required disabled={locked} />
                  </Field>
                  <Field label="Status" wide="4">
                    <Form.Select value={form.approvalStatus || 'draft'} onChange={setField('approvalStatus')} disabled={locked}>
                      <option value="draft">Draft</option>
                      <option value="approved">Approved</option>
                    </Form.Select>
                  </Field>
                  <Field label="Type" wide="8">
                    <Form.Control value={form.classification || ''} onChange={setField('classification')} disabled={locked} placeholder="Technical, HSE…" />
                  </Field>
                  <Field label="Version" wide="4">
                    <Form.Control value={form.version || ''} onChange={setField('version')} disabled={locked} />
                  </Field>
                  <Field label="Content" wide="12">
                    <Form.Control as="textarea" rows={8} value={form.content || ''} onChange={setField('content')} disabled={locked} />
                  </Field>
                  {!locked ? (
                    <div className="cust-field cust-field--12">
                      <Button type="button" size="sm" variant="outline-primary" disabled={saving || assisting} onClick={suggestWording}>
                        Suggest wording
                      </Button>
                    </div>
                  ) : null}
                </div>
              </section>
            ) : null}

            {kind === 'credentials' ? (
              <section className="cust-section cred-band">
                <header className="cust-section__head">
                  <span>{locked ? '02' : '03'}</span>
                  <div>
                    <h2>Named on tenders</h2>
                    <p>Open pursuits that will cite this credential. Submitted, awarded, and lost tenders stay off this list.</p>
                  </div>
                </header>
                {isCreate ? (
                  <p className="cred-next">Save this credential, then attach it to open pursuits from the edit page.</p>
                ) : (
                  <>
                    {links.length ? (
                      <ul className="pers-named">
                        {links.map((item) => (
                          <li key={item._id}>
                            <span>
                              <strong>{item.tenderReference || 'Tender'}</strong>
                              {item.tenderTitle ? ` — ${item.tenderTitle}` : ''}
                            </span>
                            {isEdit ? (
                              <button type="button" onClick={() => detach(item._id)} disabled={saving}>Remove</button>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-muted">This credential is not attached to an active tender yet.</p>
                    )}
                    {isEdit ? (
                      <div className="cust-fields pers-named__add">
                        <Field label="Active tenders" wide="8">
                          <MasterLookup
                            multiple
                            options={tenderOptions}
                            value={attachTenders}
                            onChange={setAttachTenders}
                            placeholder={tenderOptions.length ? 'Search open pursuits' : 'No further active tenders'}
                            disabled={!tenderOptions.length}
                          />
                        </Field>
                        <div className="cust-field cust-field--4" style={{ alignSelf: 'end' }}>
                          <Button type="button" size="sm" variant="primary" onClick={attach} disabled={saving || !attachTenders.length}>
                            Attach to selected tenders
                          </Button>
                        </div>
                      </div>
                    ) : null}
                  </>
                )}
              </section>
            ) : null}
          </div>
        </div>
      </form>
    </div>
  );
};

export default KnowledgeRecordPage;
